import unblockItemContent, {
    unblockItemContentForItemAttachment,
} from '../actions/unblockItemContent';
import loc, { format } from 'owa-localize';
import calculateAmpAvailability from 'owa-mail-amp-store/lib/utils/calculateAmpAvailability';
import { getSelectedTableView } from 'owa-mail-list-store';
import removeInfoBarMessage from 'owa-info-bar/lib/actions/removeInfoBarMessage';
import updateAmpAvailability from 'owa-mail-reading-pane-store/lib/actions/updateAmpAvailability';
import ItemViewState from 'owa-mail-reading-pane-store/lib/store/schema/ItemViewState';
import { InstrumentationContext } from 'owa-mail-store';
import { lazyMarkItemJunkNotJunkFromReadingPane } from 'owa-mail-triage-action';
import Message from 'owa-service/lib/contract/Message';
import MessageSafetyLevel from 'owa-service/lib/contract/MessageSafetyLevel';
import MessageSafetyReason from 'owa-service/lib/contract/MessageSafetyReason';
import getUserConfiguration from 'owa-session-store/lib/actions/getUserConfiguration';
import isConsumer from 'owa-session-store/lib/utils/isConsumer';
import {
    safetyMessageTrustedSender,
    safetyMessageSpamFighterTrusted,
    safetyMessageSafeSenderListed,
    safetyMessageSenderInContactsList,
    safetyMessageSafeSenderByOrg,
    safetyMessageMovedFromJunkByUserRule,
    safetyMessageJunkFilteringDisabled,
    safetyMessageMovedToJunkByUserRule,
    safetyMessageMovedToJunkByUserRuleNoRetention,
    safetyMessageMovedToJunkByUserRuleDeleteSoon,
    safetyMessageMovedToJunkByUserAction,
    safetyMessageMovedToJunkByUserActionNoRetention,
    safetyMessageMovedToJunkByUserActionDeleteSoon,
    safetyMessageMovedToJunkByOrgRule,
    safetyMessageMovedToJunkByOrgRuleNoRetention,
    safetyMessageMovedToJunkByOrgRuleDeleteSoon,
    safetyMessageMovedToJunkByClientAction,
    safetyMessageMovedToJunkByClientActionNoRetention,
    safetyMessageMovedToJunkByClientActionDeleteSoon,
    safetyMessageMovedToJunkByASFRule,
    safetyMessageMovedToJunkByASFRuleNoRetention,
    safetyMessageMovedToJunkByASFRuleDeleteSoon,
    safetyMessageMovedToJunkByBlockedSenders,
    safetyMessageMovedToJunkByBlockedSendersNoRetention,
    safetyMessageMovedToJunkByBlockedSendersDeleteSoon,
    safetyMessageMovedToJunkByService,
    safetyMessageMovedToJunkByServiceNoRetention,
    safetyMessageMovedToJunkByServiceDeleteSoon,
    safetyMessageExclusiveModeEnabledForConsumer,
    safetyMessageExclusiveModeEnabledForEnterprise,
    safetyMessagePartialContentDisabled,
    safetyMessageConsumerPhishDetected,
    safetyMessagePhishDetected,
    safetyMessageSenderFailedAuth,
    safetyMessagePhishPasswordDetected,
    safetyBarLinkAlwaysShowContent,
    safetyBarLinkAlwaysShowContentNoSender,
    safetyBarLinkTemporarilyMarkAsSafe,
    safetyBarLinkLearnAboutPhishing,
    safetyBarLinkMarkAsNotJunk,
    addTrustedSenderFailHeader,
    addTrustedSenderFailSubtext,
} from './createSafetyBarMessageViewState.locstring.json';
import {
    InfoBarMessageSource,
    InfoBarMessageRank,
    InfoBarMessageColor,
    InfoBarCustomAction,
    InfoBarMessageViewState,
} from 'owa-info-bar/lib/schema/InfoBarMessageViewState';
import { confirm } from 'owa-confirm-dialog';
import loadTrustedSendersAndDomains from 'owa-trusted-senders/lib/services/loadTrustedSendersAndDomains';
import { logUsage } from 'owa-analytics';

/* tslint:disable:no-http-string */
const learnAboutPhishingUrl = 'http://www.microsoft.com/security/online-privacy/phishing-faq.aspx';
/* tslint:enable:no-http-string */

const retentionExpirationWarningThreshold = 30;

const addToSafeListAndShowContentAction = (
    message: Message,
    key: string,
    itemViewState: ItemViewState
) => () => {
    if (
        message.HasBlockedImages ||
        !message.BlockStatus ||
        (message.MessageSafety &&
            message.MessageSafety.MessageSafetyLevel > MessageSafetyLevel.Safe)
    ) {
        unblockItemContent(message, false /* isTemporary */).then(
            (wasResponseSuccessful: boolean) => {
                const senderSMTP =
                    message.SenderSMTPAddress || message.Sender?.Mailbox?.EmailAddress;
                // If AddToTrustedSender call fails because user has more than the allowed limit (1024) in
                // their safe senders list, show an error dialog and prompt them to remove senders from the list
                if (!wasResponseSuccessful && senderSMTP) {
                    confirm(
                        loc(addTrustedSenderFailHeader),
                        format(loc(addTrustedSenderFailSubtext), senderSMTP),
                        false /* resolveImmediately */,
                        {
                            hideCancelButton: true,
                        }
                    );
                    loadTrustedSendersAndDomains().then((trustedSendersAndDomains: string[]) => {
                        logUsage('RPInfobarShowTrustedSenderError', [
                            trustedSendersAndDomains.length,
                        ]);
                    });
                }
                // The Amp view state needs to be re-created when an item becomes unblocked
                updateAmpAvailability(
                    itemViewState.ampViewState,
                    calculateAmpAvailability(message)
                );
            }
        );

        removeInfoBarMessage(itemViewState, key);
    }
};

const temporarilyShowContentAction = (
    message: Message,
    key: string,
    itemViewState: ItemViewState
) => () => {
    if (message.HasBlockedImages) {
        unblockItemContent(message, true /* isTemporary */).then(() => {
            // The Amp view state needs to be re-created when an item becomes unblocked
            updateAmpAvailability(itemViewState.ampViewState, calculateAmpAvailability(message));
        });

        removeInfoBarMessage(itemViewState, key);
    }
};

const temporarilyShowContentActionForItemAttachment = (
    message: Message,
    key: string,
    itemViewState: ItemViewState
) => () => {
    if (message.HasBlockedImages) {
        unblockItemContentForItemAttachment(message).then(() => {
            // The Amp view state needs to be re-created when an item becomes unblocked
            updateAmpAvailability(itemViewState.ampViewState, calculateAmpAvailability(message));
        });

        removeInfoBarMessage(itemViewState, key);
    }
};

const markAsNotJunk = (
    message: Message,
    instrumentationContexts: InstrumentationContext[]
) => () => {
    lazyMarkItemJunkNotJunkFromReadingPane.importAndExecute(
        message.ItemId.Id,
        getSelectedTableView(),
        'ReadingPane',
        instrumentationContexts
    );
};

function openLearnAboutPhishingLink() {
    window.open(learnAboutPhishingUrl, '_blank');
}

function isDeletePolicyEnabled(): boolean {
    const userConfiguration = getUserConfiguration();
    const retentionPolicyTagsCount = userConfiguration.RetentionPolicyTags
        ? userConfiguration.RetentionPolicyTags.length
        : 0;
    for (let i = 0; i < retentionPolicyTagsCount; i++) {
        if (!userConfiguration.RetentionPolicyTags[i].IsArchive) {
            return true;
        }
    }

    return false;
}

function getDaysLeftInDeletePolicy(message: Message): number {
    let daysLeft = retentionExpirationWarningThreshold + 1;
    if (message.PolicyTag && message.RetentionDate && isDeletePolicyEnabled()) {
        const retentionDate = new Date(message.RetentionDate);
        const timeLeft = retentionDate.getTime() - Date.now();
        daysLeft = Math.ceil(timeLeft / (1000 * 60 * 60 * 24));
    }
    return daysLeft;
}

function initializeCustomAction(): InfoBarCustomAction {
    return {
        text: null,
        action: null,
    };
}

function convertSafetyReasonToSafetyBarMessage(
    messageSafetyReason: MessageSafetyReason,
    daysLeftInDeletePolicy: number
): string {
    const showDaysLeft =
        daysLeftInDeletePolicy > 0 && daysLeftInDeletePolicy <= retentionExpirationWarningThreshold;
    // Either no retention policy is set or the delete policy has more days left than the warning threshold
    const showNoRetentionPolicy = daysLeftInDeletePolicy > retentionExpirationWarningThreshold;
    switch (messageSafetyReason) {
        case MessageSafetyReason.TrustedSender:
            return loc(safetyMessageTrustedSender);
        case MessageSafetyReason.SpamFighterTrusted:
            return loc(safetyMessageSpamFighterTrusted);
        case MessageSafetyReason.SafeSenderListed:
            return loc(safetyMessageSafeSenderListed);
        case MessageSafetyReason.SenderInContactsList:
            return loc(safetyMessageSenderInContactsList);
        case MessageSafetyReason.SafeSenderByOrg:
            return loc(safetyMessageSafeSenderByOrg);
        case MessageSafetyReason.MovedFromJunkByUserRule:
            return loc(safetyMessageMovedFromJunkByUserRule);
        case MessageSafetyReason.JunkFilteringDisabled:
            return loc(safetyMessageJunkFilteringDisabled);
        case MessageSafetyReason.MovedToJunkByUserRule:
            if (showDaysLeft) {
                return format(loc(safetyMessageMovedToJunkByUserRule), daysLeftInDeletePolicy);
            } else if (showNoRetentionPolicy) {
                return loc(safetyMessageMovedToJunkByUserRuleNoRetention);
            } else {
                // Has 0 or less days left in retention policy (will be cleaned next time mailbox assistant runs)
                return loc(safetyMessageMovedToJunkByUserRuleDeleteSoon);
            }
        case MessageSafetyReason.MovedToJunkByUserAction:
            if (showDaysLeft) {
                return format(loc(safetyMessageMovedToJunkByUserAction), daysLeftInDeletePolicy);
            } else if (showNoRetentionPolicy) {
                return loc(safetyMessageMovedToJunkByUserActionNoRetention);
            } else {
                // Has 0 or less days left in retention policy (will be cleaned next time mailbox assistant runs)
                return loc(safetyMessageMovedToJunkByUserActionDeleteSoon);
            }
        case MessageSafetyReason.MovedToJunkByOrgRule:
            if (showDaysLeft) {
                return format(loc(safetyMessageMovedToJunkByOrgRule), daysLeftInDeletePolicy);
            } else if (showNoRetentionPolicy) {
                return loc(safetyMessageMovedToJunkByOrgRuleNoRetention);
            } else {
                // Has 0 or less days left in retention policy (will be cleaned next time mailbox assistant runs)
                return loc(safetyMessageMovedToJunkByOrgRuleDeleteSoon);
            }
        case MessageSafetyReason.MovedToJunkByClientAction:
            if (showDaysLeft) {
                return format(loc(safetyMessageMovedToJunkByClientAction), daysLeftInDeletePolicy);
            } else if (showNoRetentionPolicy) {
                return loc(safetyMessageMovedToJunkByClientActionNoRetention);
            } else {
                // Has 0 or less days left in retention policy (will be cleaned next time mailbox assistant runs)
                return loc(safetyMessageMovedToJunkByClientActionDeleteSoon);
            }
        case MessageSafetyReason.MovedToJunkByASFRule:
            if (showDaysLeft) {
                return format(loc(safetyMessageMovedToJunkByASFRule), daysLeftInDeletePolicy);
            } else if (showNoRetentionPolicy) {
                return loc(safetyMessageMovedToJunkByASFRuleNoRetention);
            } else {
                // Has 0 or less days left in retention policy (will be cleaned next time mailbox assistant runs)
                return loc(safetyMessageMovedToJunkByASFRuleDeleteSoon);
            }
        case MessageSafetyReason.MovedToJunkByBlockedSenders:
            if (showDaysLeft) {
                return format(
                    loc(safetyMessageMovedToJunkByBlockedSenders),
                    daysLeftInDeletePolicy
                );
            } else if (showNoRetentionPolicy) {
                return loc(safetyMessageMovedToJunkByBlockedSendersNoRetention);
            } else {
                // Has 0 or less days left in retention policy (will be cleaned next time mailbox assistant runs)
                return loc(safetyMessageMovedToJunkByBlockedSendersDeleteSoon);
            }
        case MessageSafetyReason.MovedToJunkByService:
            if (showDaysLeft) {
                return format(loc(safetyMessageMovedToJunkByService), daysLeftInDeletePolicy);
            } else if (showNoRetentionPolicy) {
                return loc(safetyMessageMovedToJunkByServiceNoRetention);
            } else {
                // Has 0 or less days left in retention policy (will be cleaned next time mailbox assistant runs)
                return loc(safetyMessageMovedToJunkByServiceDeleteSoon);
            }
        case MessageSafetyReason.ExclusiveModeEnabled:
            return isConsumer()
                ? loc(safetyMessageExclusiveModeEnabledForConsumer)
                : loc(safetyMessageExclusiveModeEnabledForEnterprise);
        case MessageSafetyReason.PartialContentDisabled:
            return loc(safetyMessagePartialContentDisabled);
        case MessageSafetyReason.PhishDetected:
            return isConsumer()
                ? loc(safetyMessageConsumerPhishDetected)
                : loc(safetyMessagePhishDetected);
        case MessageSafetyReason.SenderFailedAuth:
            return loc(safetyMessageSenderFailedAuth);
        case MessageSafetyReason.PhishPasswordDetected:
            return loc(safetyMessagePhishPasswordDetected);
        default:
            return null;
    }
}

function getSafetyBarMessageColor(safetyLevel: MessageSafetyLevel): InfoBarMessageColor {
    switch (safetyLevel) {
        case MessageSafetyLevel.Trusted:
        case MessageSafetyLevel.Safe:
            return InfoBarMessageColor.Green;
        case MessageSafetyLevel.Unknown:
            return InfoBarMessageColor.Yellow;
        case MessageSafetyLevel.Suspicious:
            return InfoBarMessageColor.Red;
        default:
            return InfoBarMessageColor.Grey;
    }
}

export default function createSafetyBarMessageViewState(
    message: Message,
    key: string,
    instrumentationContexts: InstrumentationContext[],
    itemViewState: ItemViewState,
    isItemAttachment: boolean
): InfoBarMessageViewState {
    if (message && (message.MessageSafety || message.HasBlockedImages)) {
        const firstCustomAction = initializeCustomAction();
        const secondCustomAction = initializeCustomAction();
        let safetyReason: MessageSafetyReason;
        let safetyLevel: MessageSafetyLevel;

        if (message.MessageSafety) {
            safetyReason = message.MessageSafety.MessageSafetyReason;
            safetyLevel = message.MessageSafety.MessageSafetyLevel;
        } else {
            safetyReason = MessageSafetyReason.PartialContentDisabled;
            safetyLevel = MessageSafetyLevel.Unknown;
        }

        const daysLeftInDeletePolicy = getDaysLeftInDeletePolicy(message);
        const safetyMessage = convertSafetyReasonToSafetyBarMessage(
            safetyReason,
            daysLeftInDeletePolicy
        );
        if (!safetyMessage) {
            // No need to show safety bar if safety message is null.
            return null;
        }

        switch (safetyReason) {
            case MessageSafetyReason.PartialContentDisabled:
                if (!isItemAttachment) {
                    // VSO #25803. Sender appears to be rarely undefined. When this happens, fallback to an empty string to avoid component errors.
                    const emailAddress = message.From?.Mailbox?.EmailAddress
                        ? message.From.Mailbox.EmailAddress
                        : '';
                    firstCustomAction.text = emailAddress
                        ? format(loc(safetyBarLinkAlwaysShowContent), emailAddress.toLowerCase())
                        : loc(safetyBarLinkAlwaysShowContentNoSender);
                    firstCustomAction.action = addToSafeListAndShowContentAction(
                        message,
                        key,
                        itemViewState
                    );
                    if (message.HasBlockedImages) {
                        secondCustomAction.text = loc(safetyBarLinkTemporarilyMarkAsSafe);
                        secondCustomAction.action = temporarilyShowContentAction(
                            message,
                            key,
                            itemViewState
                        );
                    }
                } else {
                    firstCustomAction.text = loc(safetyBarLinkTemporarilyMarkAsSafe);
                    firstCustomAction.action = temporarilyShowContentActionForItemAttachment(
                        message,
                        key,
                        itemViewState
                    );
                }
                break;
            case MessageSafetyReason.PhishDetected:
            case MessageSafetyReason.PhishPasswordDetected:
                firstCustomAction.text = loc(safetyBarLinkLearnAboutPhishing);
                firstCustomAction.action = openLearnAboutPhishingLink;
                if (message.HasBlockedImages) {
                    secondCustomAction.text = loc(safetyBarLinkTemporarilyMarkAsSafe);
                    secondCustomAction.action = temporarilyShowContentAction(
                        message,
                        key,
                        itemViewState
                    );
                }
                break;
            case MessageSafetyReason.SenderFailedAuth:
                if (message.HasBlockedImages) {
                    secondCustomAction.text = loc(safetyBarLinkTemporarilyMarkAsSafe);
                    secondCustomAction.action = temporarilyShowContentAction(
                        message,
                        key,
                        itemViewState
                    );
                }
                break;
            case MessageSafetyReason.MovedToJunkByUserRule:
            case MessageSafetyReason.MovedToJunkByUserAction:
            case MessageSafetyReason.MovedToJunkByOrgRule:
            case MessageSafetyReason.MovedToJunkByClientAction:
            case MessageSafetyReason.MovedToJunkByASFRule:
            case MessageSafetyReason.MovedToJunkByBlockedSenders:
            case MessageSafetyReason.MovedToJunkByService:
            case MessageSafetyReason.ExclusiveModeEnabled:
                if (getUserConfiguration().SessionSettings.IsShadowMailbox) {
                    // don't show safety bar when the action is to mark as not junk
                    return null;
                }
                firstCustomAction.text = loc(safetyBarLinkMarkAsNotJunk);
                firstCustomAction.action = markAsNotJunk(message, instrumentationContexts);
                if (message.HasBlockedImages) {
                    secondCustomAction.text = loc(safetyBarLinkTemporarilyMarkAsSafe);
                    secondCustomAction.action = temporarilyShowContentAction(
                        message,
                        key,
                        itemViewState
                    );
                }
                break;
        }

        const safetyInfoBarMessageViewState: InfoBarMessageViewState = {
            key: key,
            source: InfoBarMessageSource.ReadingPane,
            rank: InfoBarMessageRank.Safety,
            color: getSafetyBarMessageColor(safetyLevel),
            message: safetyMessage,
            messageParts: [safetyMessage],
        };

        // VSO 13786: Refactoring info bar so all non-action props are in store
        if (firstCustomAction.text && firstCustomAction.action) {
            safetyInfoBarMessageViewState.messageParts.push(firstCustomAction);
        }

        if (secondCustomAction.text && secondCustomAction.action) {
            safetyInfoBarMessageViewState.messageParts.push('|');
            safetyInfoBarMessageViewState.messageParts.push(secondCustomAction);
        }

        return safetyInfoBarMessageViewState;
    } else {
        return null;
    }
}
